import {
  Avatar,
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { buttonColors, colors } from "../../../utility/types/constant";
import CloseIcon from "@mui/icons-material/Close";
import { Field, Form, Formik } from "formik";
import { cancelPaymentMethodValidationForm } from "../../../utility/validation/subscriptionValidation";
import {
  ICancelMemberSubscription,
  SubscriptionStatus,
} from "../../../utility/types/subscriptionType";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import { useNavigate } from "react-router-dom";
import { selectCurrentUser } from "../../../redux/selectors/userSelector";
import {
  selectError,
  selectIsLoading,
  selectUserSubscription,
} from "../../../redux/selectors/subscriptionSelector";
import { cancelMembership } from "../../../redux/actions/subscriptionAction";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import NotificationModal from "../notification-modal/NotificationModal";
import { StyledTypography } from "../../circle-leader-application/styles";
import { format, isValid } from "date-fns";

interface CancelMembershipSubscriptionProps {
  open: boolean;
  handleClose: () => void;
  //   endDate: any;
}

const CancelMembershipSubscription = (
  props: CancelMembershipSubscriptionProps
) => {
  const { open, handleClose } = props;

  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();

  const userData = useSelector(selectCurrentUser);

  const userSubscription = useSelector(selectUserSubscription);

  const isLoading = useSelector(selectIsLoading);

  const isError = useSelector(selectError);

  const [alert, setAlert] = useState({ type: "", message: "" });

  const [openNotificationModal, setOpenNotificationModal] = useState(false);

  useEffect(() => {
    if (isError) {
      setAlert({ type: "isError", message: isError });
    }
  }, [isError]);

  const initialValues: ICancelMemberSubscription = {
    userId: userData?._id,
    cancellationReason: "",
    subscriptionId: userSubscription?._id ?? "",
  };

  const handleCancelMembership = async (values: ICancelMemberSubscription) => {
    try {
      const payload = {
        userId: userData?._id,
        cancellationReason: values.cancellationReason,
        subscriptionId: userSubscription?._id ?? "",
      };

      const result = await dispatch(cancelMembership({ payload })).unwrap();

      if (result.stripeCustomerId) {
        setAlert({
          type: "success",
          message: "Membership Subscription Cancelled Successfully!",
        });

        setOpenNotificationModal(true);

        setTimeout(() => {
          setOpenNotificationModal(false);
          handleClose();
          navigate(`/user/${userData?._id}`);
        }, 10000);
      } else {
        setAlert({
          type: "error",
          message: "Membership Subscription Cancellation Failed",
        });
        setOpenNotificationModal(false);
      }
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const displayDate = new Date(userSubscription?.endDate);

  const showEndDate = isValid(displayDate)
    ? format(displayDate, "MMMM d, yyyy")
    : "N/A";

  return (
    <React.Fragment>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogContent>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: colors.white,
              backgroundColor: colors.cancel,
              "&:hover": {
                color: colors.white,
                backgroundColor: colors.cancel,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography component="h1" variant="h4" align="center" sx={{ mb: 0 }}>
            Cancel Your Membership Subscription
          </Typography>
        </DialogContent>
        <Container component="main" maxWidth="sm" sx={{ mb: 1 }}>
          <Paper
            variant="outlined"
            sx={{
              my: { xs: 3, md: 4 },
              p: { xs: 2, md: 3 },
              width: "100%",
            }}
          >
            {userSubscription?.subscriptionStatus ===
              SubscriptionStatus.pendingCancellation && (
              <Typography
                component="div"
                variant="body1"
                align="center"
                sx={{
                  mb: 3,
                  color: "text.secondary",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "1.1rem",
                }}
              >
                We're truly heartbroken to see you go. Your subscription has
                been successfully cancelled, but you'll still have access to
                your perks until{" "}
                <strong style={{ color: "yellowgreen" }}>{showEndDate}</strong>
                .
                <br />
                This gives you some time to reconsider or make the most out of
                the remaining period. We hope to welcome you back in the future!
                <br />
                Also, keep in mind that you are welcome to reactivate your
                membership subscription anytime by clicking on the{" "}
                <strong>Activate Membership</strong> in your panel
              </Typography>
            )}

            {userSubscription?.subscriptionStatus !==
              SubscriptionStatus.pendingCancellation && (
              <Typography
                component="div"
                variant="body1"
                align="center"
                sx={{
                  mb: 3,
                  color: "text.secondary",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "1.1rem",
                }}
              >
                Upon cancelling your membership you will loose access to some of
                services. However, you'll still have access to your perks until
                then end of your membership subscription period{" "}
                <strong style={{ color: "yellowgreen" }}>{showEndDate}</strong>
                .
                <br />
                This gives you some time to reconsider or make the most out of
                the remaining period. We hope to welcome you back in the future!
              </Typography>
            )}
            <Grid item xs={12} md={6} lg={8} sx={{ padding: 2 }}>
              <Formik
                initialValues={initialValues}
                onSubmit={handleCancelMembership}
                validationSchema={cancelPaymentMethodValidationForm}
              >
                <Form>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={12}>
                      <Field name="cancellationReason">
                        {({ field, form }: any) => (
                          <TextField
                            {...field}
                            required
                            id="cancellationReason"
                            label="Please provide a cancellation reason"
                            fullWidth
                            multiline
                            rows={4}
                            error={
                              form.touched.cancellationReason
                                ?.cancellationReason &&
                              Boolean(
                                form.errors.cancellationReason
                                  ?.cancellationReason
                              )
                            }
                            helperText={
                              form.touched.cancellationReason
                                ?.cancellationReason &&
                              form.errors.cancellationReason?.cancellationReason
                            }
                            sx={{
                              fontSize: "1.2rem",
                              "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                  borderColor: "black",
                                },
                                "&:hover fieldset": {
                                  borderColor: "black",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: "black",
                                },
                              },
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      justifyContent="center"
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Button
                        size="large"
                        disabled={
                          isLoading ||
                          userSubscription?.subscriptionStatus ===
                            SubscriptionStatus.pendingCancellation
                        }
                        variant="contained"
                        type="submit"
                        sx={{
                          color: colors.white,
                          backgroundColor: buttonColors.infoDarkBlue,
                          "&:hover": {
                            backgroundColor: buttonColors.infoDarkBlue,
                          },
                          width: 250,
                          fontSize: "1.1rem",
                        }}
                      >
                        {isLoading ? (
                          <LoadingSpinner
                            loading={isLoading}
                            color={colors.white}
                          />
                        ) : (
                          "Cancel Subscription"
                        )}
                      </Button>
                    </Grid>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "red", // Use red for errors
                          textAlign: "center",
                        }}
                      >
                        {alert?.type === "error" &&
                          "An error occurred while processing payment. Please try again."}
                      </Typography>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "green",
                          textAlign: "center",
                          mt: 3,
                        }}
                      >
                        {alert?.type === "success" && alert.message}
                      </Typography>
                    </Box>
                  </Grid>
                </Form>
              </Formik>
            </Grid>
          </Paper>
        </Container>
      </Dialog>

      {openNotificationModal && (
        <NotificationModal
          open={openNotificationModal}
          handleClose={() => setOpenNotificationModal(false)}
          title="Membership Subscription Cancelled Successfully!"
        >
          <Grid item>
            <Avatar
              src="/assets/icons/success-animated-icon.gif"
              sx={{ width: 30, height: 30 }}
              alt="Congrats Icon"
            />
          </Grid>
          <StyledTypography
            sx={{ fontSize: "1.1rem", color: "text.secondary" }}
          >
            Your Membership Subscription is now cancelled.We're truly
            heartbroken to see you go. Thank you for being part of the ForUs
            community.
          </StyledTypography>
        </NotificationModal>
      )}
    </React.Fragment>
  );
};

CancelMembershipSubscription.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  endDate: PropTypes.any,
};

export default CancelMembershipSubscription;
